/**
 * ── THE SHARED GLTF LOADER (plan §4.1 `loaders/gltf.ts`) ────────────────────────────────────────
 *
 * ONE `GLTFLoader` with the Meshopt decoder registered — the exact setup `entities/staticMesh.ts`
 * makes for itself (`loader.setMeshoptDecoder(MeshoptDecoder)`), so a cooked `EXT_meshopt_
 * compression` GLB from `cook:asset` parses here as it does on the world path; a plain GLB
 * ignores the decoder. Parsing is from an `ArrayBuffer` the service fetched (so the service owns
 * abort, verify and the scheduler slot), never `loader.load(url)`.
 *
 * `parseGltf` returns the scene AND the tracked GPU resources under it — geometries, materials,
 * textures, collected by object identity — because disposal must walk exactly what the parse
 * created and nothing more. Walking `mesh.material` at dispose time is how the GLTFLoader
 * shared-default-material trap bites (`staticMesh.ts:184-189`): one parse's stripped subtree
 * disposes a material another mesh of the SAME parse still draws. Tracking per parse and
 * disposing the SET makes the unit of disposal the source, which is the plan's refcount unit.
 *
 * KTX2 is NOT here (rendering R2): a `ktx2` file format is gated `unsupported` by the service
 * through `requires.formats`; a `KTX2Loader` lands as a lazy import behind that gate.
 *
 * EAGER-SAFE by the same argument as the plan's §4.1: GLTFLoader is already in the main chunk via
 * `characterView.ts`, so this module adds the decoder registration and a walk, not a loader.
 */

import * as THREE from 'three/webgpu';
import { GLTFLoader, type GLTF } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';

import type { TrackedResources } from '../handle';

const loader = new GLTFLoader();
loader.setMeshoptDecoder(MeshoptDecoder);

export interface ParsedGltf {
  scene: THREE.Group;
  animations: THREE.AnimationClip[];
  tracked: TrackedResources;
  /** A `SkinnedMesh` is under the root — `Object3D.clone()` would share its skeleton. */
  skinned: boolean;
  /** Rendered meshes under the root (a clip GLB's preview mesh counts here — `loaders/clip.ts` discards it). */
  meshes: number;
}

/**
 * Parse GLB bytes. `baseUrl` is what `GLTFLoader.load` would compute for itself
 * (`LoaderUtils.extractUrlBase`) — pass the file's directory URL so a non-self-contained asset
 * (an external `.bin`) still resolves; `''` is right for a packed single-file GLB.
 * Throws on malformed bytes — the SERVICE maps that to a failure state; nothing here is public.
 */
export async function parseGltf(buf: ArrayBuffer, baseUrl = ''): Promise<ParsedGltf> {
  const gltf: GLTF = await loader.parseAsync(buf, baseUrl);
  const tracked = trackResources(gltf.scene);
  let skinned = false;
  let meshes = 0;
  gltf.scene.traverse((o) => {
    if ((o as THREE.Mesh).isMesh) meshes += 1;
    if ((o as THREE.SkinnedMesh).isSkinnedMesh) skinned = true;
  });
  return { scene: gltf.scene, animations: gltf.animations, tracked, skinned, meshes };
}

/** Everything on a material that quacks like a texture — slot names are not enumerated on purpose (the census's rule). */
function texturesOf(material: THREE.Material, out: Set<THREE.Texture>): void {
  for (const value of Object.values(material as unknown as Record<string, unknown>)) {
    if (value !== null && typeof value === 'object' && (value as { isTexture?: boolean }).isTexture === true) {
      out.add(value as THREE.Texture);
    }
  }
}

/** Collect the GPU resources under `root`, by identity, so a shared material or texture is one entry. */
export function trackResources(root: THREE.Object3D): TrackedResources {
  const tracked: TrackedResources = { geometries: new Set(), materials: new Set(), textures: new Set() };
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    if (mesh.geometry) tracked.geometries.add(mesh.geometry);
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const m of mats) {
      if (!m) continue;
      tracked.materials.add(m);
      texturesOf(m, tracked.textures);
    }
  });
  return tracked;
}

/** The disposal walk: geometries, materials, then textures. Idempotent (three's `dispose` is). */
export function disposeTracked(tracked: TrackedResources): void {
  for (const g of tracked.geometries) g.dispose();
  for (const m of tracked.materials) m.dispose();
  for (const t of tracked.textures) t.dispose();
}
